import { createElement, css } from '@emotion/react';
import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { useTheme } from '../../hooks/useTheme.js';
import { u as useOverlaysStore, a as CLOSE_MODAL } from '../../providers/overlaysContext/overlaysContext.js';
import { convertSxToEmotionStyles } from '../../utils/sxToEmotionStyles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { useEventListener } from '../../hooks/useEventListener.js';

function getModalPosition(position, base) {
  var sx = {
    transform: ""
  };
  switch (position.x) {
    case "left":
      sx.left = base.spacing.x12;
      break;
    case "right":
      sx.right = base.spacing.x12;
      break;
    case "center":
    default:
      sx.left = "50%";
      sx.translate = "-50%";
      break;
  }
  switch (position.y) {
    case "top":
      sx.top = base.spacing.x12;
      break;
    case "bottom":
      sx.bottom = base.spacing.x12;
      break;
    case "center":
    default:
      sx.top = "50%";
      sx.translate += " -50%";
      break;
  }
  return sx;
}
function scaleWithBounce(motionProfile, base) {
  return {
    initial: {
      scale: 0.8,
      opacity: 0
    },
    animate: {
      scale: 1,
      opacity: 1
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        ease: base.motion.normal[motionProfile].jsEase
      }
    },
    transition: {
      duration: base.motion.bounce[motionProfile].jsDuration,
      ease: base.motion.bounce[motionProfile].jsEase,
      opacity: {
        ease: base.motion.normal[motionProfile].jsEase
      }
    }
  };
}
function fade(motionProfile, base) {
  return {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: base.motion.normal[motionProfile].jsDuration,
      ease: base.motion.normal[motionProfile].jsEase
    }
  };
}
function getModalAnimationProps(motionProfile, motionPattern, base) {
  switch (motionProfile) {
    case "none":
      return {};
    case "fast":
    case "gentle":
      return motionPattern === "fade" ? fade(motionProfile, base) : scaleWithBounce(motionProfile, base);
  }
}

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ModalOverlay(_ref) {
  var hasExternalContainer = _ref.hasExternalContainer;
  var _useTheme = useTheme(),
    base = _useTheme.base;
  var _useOverlaysStore = useOverlaysStore(function (state) {
      return state.modalList;
    }),
    modalList = _useOverlaysStore.state,
    dispatchAction = _useOverlaysStore.dispatchAction;
  useEventListener("keydown", function (event) {
    if (event.key === "Escape" && modalList.length > 0) {
      var topModal = modalList[modalList.length - 1];
      if (topModal.escapeKeyClose) {
        var _topModal$onCloseModa;
        (_topModal$onCloseModa = topModal.onCloseModal) === null || _topModal$onCloseModa === void 0 ? void 0 : _topModal$onCloseModa.call(topModal);
        if (!topModal.isControlled) {
          dispatchAction({
            type: CLOSE_MODAL,
            payload: {
              id: topModal.id
            }
          });
        }
      }
    }
  }, {
    current: document.body
  });
  return jsx(AnimatePresence, {
    children: modalList.map(function (modal) {
      var positionSx = getModalPosition(modal.position, base);
      var modalBgStyles = convertSxToEmotionStyles(modal.bgOverlaySx, {
        base: base
      });
      var modalAnimationProps = getModalAnimationProps(modal.motionProfile, modal.motionPattern, base);
      return jsxs(Fragment, {
        children: [jsx(motion.div, _objectSpread({
          "data-testid": "".concat(modal.testId, "__bgOverlay"),
          onClick: function onClick() {
            if (modal.outsideClicksClose) {
              var _modal$onCloseModal;
              (_modal$onCloseModal = modal.onCloseModal) === null || _modal$onCloseModal === void 0 ? void 0 : _modal$onCloseModal.call(modal);
              if (!modal.isControlled) {
                dispatchAction({
                  type: CLOSE_MODAL,
                  payload: {
                    id: modal.id
                  }
                });
              }
            }
          },
          css: merge({
            position: hasExternalContainer ? "absolute" : "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: modal.showBgOverlay ? base.color.overlay : "",
            zIndex: base.zLevel.modal
          }, modalBgStyles)
        }, modal.motionProfile !== "none" ? {
          initial: {
            opacity: 0
          },
          animate: {
            opacity: typeof modal.bgOverlaySx.opacity === "number" || typeof modal.bgOverlaySx.opacity === "string" ? modal.bgOverlaySx.opacity : 1
          },
          exit: {
            opacity: 0
          },
          transition: {
            duration: base.motion.normal[modal.motionProfile].jsDuration,
            ease: base.motion.normal[modal.motionProfile].jsEase
          }
        } : {}), "".concat(modal.id, "__bgOverlay")), createElement(motion.div, _objectSpread(_objectSpread({}, modalAnimationProps), {}, {
          key: "".concat(modal.id, "__modalContent"),
          "data-testid": "".concat(modal.testId, "__modalContent"),
          css: /*#__PURE__*/css(_objectSpread(_objectSpread({}, positionSx), {}, {
            position: hasExternalContainer ? "absolute" : "fixed",
            zIndex: base.zLevel.modal,
            display: "flex",
            flexDirection: "column"
          }, modal.fullScreenUntilBreakpoint ? _defineProperty({}, "@media screen and (max-width: ".concat(base.breakpoint[modal.fullScreenUntilBreakpoint], "px)"), {
            top: "0px",
            left: "0px",
            bottom: "0px",
            right: "0px",
            translate: "0 0",
            "& .ModalContent": {
              borderRadius: "0px"
            }
          }) : {}), process.env.NODE_ENV === "production" ? "" : ";label:ModalOverlay;", process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */")
        }), modal.content)]
      }, modal.id);
    })
  });
}

export { ModalOverlay as M };
//# sourceMappingURL=ModalOverlay.js.map
